import React from "react";
import { usePage } from "../hooks/useApi";
import { pageApi } from "../services/api";
import { Building2, Home, Wrench } from "lucide-react";
import { Link } from "react-router-dom";

const OurServices: React.FC = () => {
  const { data: page, isLoading, error } = usePage("about");

  // Show loading state
  if (isLoading) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-200 rounded-2xl h-64"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return null; // Fail silently for this section
  }

  // Helper function to get field value with fallback
  const getField = (key: string, fallback: string = ""): string => {
    if (!page) return fallback;
    const fields = pageApi.getPageFields(page);
    return fields[key] || fallback;
  };

  const services = [
    {
      icon: Building2,
      title: getField("service1_title", "New Home Construction"),
      description: getField(
        "service1_description",
        "From concept to completion, we build your dream home with quality craftsmanship and attention to detail."
      ),
      features: [
        "Custom home designs",
        "Quality materials",
        "Timely completion",
        "Full project management",
      ],
    },
    {
      icon: Home,
      title: getField("service2_title", "Home Extensions"),
      description: getField(
        "service2_description",
        "Expand your living space with seamless extensions that blend perfectly with your existing home."
      ),
      features: [
        "Seamless integration",
        "Increased living space",
        "Modern design",
        "Value enhancement",
      ],
    },
    {
      icon: Wrench,
      title: getField("service3_title", "Renovations & Remodeling"),
      description: getField(
        "service3_description",
        "Transform your existing space with expert renovations that breathe new life into your home."
      ),
      features: [
        "Kitchen renovations",
        "Bathroom upgrades",
        "Interior remodeling",
        "Modern finishes",
      ],
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-[#FAFAFA] to-white">
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
            {getField("services_section_title", "What We Do")}
          </h2>
          <p className="text-lg text-[#334155] max-w-3xl mx-auto leading-relaxed">
            {getField(
              "services_section_subtitle",
              "From new home construction to extensions and renovations, we provide comprehensive building services across Canterbury. Our experienced team handles every aspect of your project with expertise and care."
            )}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <div className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF5A1F] to-[#FF6B35] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-[#1F2937] mb-4">
                  {service.title}
                </h3>

                <p className="text-[#334155] mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center space-x-3"
                    >
                      <div className="w-2 h-2 bg-[#FF5A1F] rounded-full flex-shrink-0"></div>
                      <span className="text-[#334155] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* <div className="flex items-center text-[#FF5A1F] font-semibold group-hover:text-[#e4501c] transition-colors duration-300">
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </div> */}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-[#FF5A1F] to-[#FF6B35] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              {getField("services_cta_title", "Ready to Start Your Project?")}
            </h3>
            <p className="text-lg mb-6 opacity-95">
              {getField(
                "services_cta_subtitle",
                "Whether you're building new, extending, or renovating, we're here to bring your vision to life. Contact us today for a free consultation."
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={getField("services_cta_button1_link", "/contact")}
                className="bg-white text-[#FF5A1F] px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                {getField("services_cta_button1_text", "Get Free Quote")}
              </Link>
              <Link
                to={getField("services_cta_button2_link", "/about")}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-[#FF5A1F] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                {getField("services_cta_button2_text", "Learn More")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurServices;
